import { useEffect } from 'react';

import { useStable } from 'src/editor/helpers/useStable';
import { Apollo } from 'src/gql/generated';

const RUNNING_POLL_MS = 5_000;
const IDLE_POLL_MS = 15_000;

/**
 * AI questions queued or running on the report documents in scope. Polls
 * faster while something runs so the Run buttons flip back soon after the
 * last answer lands; the start and stop mutations refetch it directly.
 */
export function useRunningQuestionIds({
  filterModel,
  questionIds,
  reportId,
  skip = false,
}: {
  filterModel: unknown;
  reportId: string | null;
  questionIds?: string[];
  skip?: boolean;
}): { loading: boolean; runningQuestionIds: string[] } {
  const isSkipped = skip || !reportId;
  const filterTimeZone = useStable(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const { data, loading, startPolling, stopPolling } = Apollo.useGetRunningQuestionIdsQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: isSkipped,
    variables: {
      filterModel: filterModel ?? null,
      filterTimeZone,
      questionIds: questionIds ?? null,
      reportId: reportId ?? '',
    },
  });
  const runningQuestionIds = data?.report?.runningQuestionIds ?? [];
  const isRunning = runningQuestionIds.length > 0;

  useEffect(() => {
    if (isSkipped) return;
    startPolling(isRunning ? RUNNING_POLL_MS : IDLE_POLL_MS);
    return stopPolling;
  }, [isRunning, isSkipped, startPolling, stopPolling]);

  return { loading, runningQuestionIds };
}
